import { Component, Input, OnDestroy } from '@angular/core';
import { SubscriptionList } from '@vendasta/rx-utils';
import { SwitchingWorkState, WorkStates } from '@vendasta/rx-utils/work-state';
import dayjs from 'dayjs';
import { Observable, ReplaySubject, combineLatest, firstValueFrom, timer } from 'rxjs';
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { LocalTimeCardService, LocalTimeObj } from './localtime-card.service';

import { Router } from '@angular/router';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
  selector: 'sales-ui-localtime-card',
  styleUrls: ['localtime-card.component.scss'],
  templateUrl: 'localtime-card.component.html',
  standalone: false,
})
export class LocaltimeCardComponent implements OnDestroy {
  private accountGroupId$$ = new ReplaySubject<string>(1);
  private accountGroupId$ = this.accountGroupId$$.pipe(distinctUntilChanged());
  @Input() set accountGroupId(id: string) {
    this.accountGroupId$$.next(id);
  }

  private editTimezoneUrl$$ = new ReplaySubject<string>(1);
  readonly editTimezoneUrl$ = this.editTimezoneUrl$$.pipe(distinctUntilChanged());
  @Input() set editTimezoneUrl(url: string) {
    this.editTimezoneUrl$$.next(url);
  }
  readonly shouldAddTimezone$: Observable<boolean>;

  private readonly subscriptions = SubscriptionList.new();
  public localTime$: Observable<LocalTimeObj>;
  private readonly timeState$ = new SwitchingWorkState<string, LocalTimeObj>((agid: string) => {
    return this.localTimeCardService.getLocalTime(agid).pipe(take(1));
  });
  private readonly timeState: WorkStates<LocalTimeObj> = this.timeState$;
  readonly loading$ = this.timeState.isLoading$;
  readonly success$ = this.timeState.isSuccess$;

  constructor(
    private readonly localTimeCardService: LocalTimeCardService,
    private readonly router: Router,
  ) {
    this.subscriptions.add(
      this.accountGroupId$.pipe(
        tap((agid) => {
          this.timeState$.startWork(agid);
        }),
      ),
    );
    this.subscriptions.add(
      (this.localTime$ = combineLatest([this.timeState.workResults$, timer(0, 1000)]).pipe(
        map(([timeObj]: [LocalTimeObj, null]) => {
          if (!timeObj || !timeObj.timezone) {
            // if no timezone don't show anything
            return <LocalTimeObj>{};
          }

          return <LocalTimeObj>{
            time: dayjs().tz(timeObj.timezone).format('MMM DD, YYYY - h:mm:ss A [GMT]Z'),
            address: timeObj.address,
            timezone: timeObj.timezone,
          };
        }),
      )),
    );

    this.shouldAddTimezone$ = combineLatest([this.editTimezoneUrl$, this.localTime$]).pipe(
      map(([editUrl, localTime]) => editUrl && !localTime?.timezone),
    );
  }

  retryLoading(): void {
    firstValueFrom(this.accountGroupId$).then((agid) => {
      this.timeState$.startWork(agid);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }
}
